import { useState, useMemo, useEffect } from 'react';
import { Pencil, X, Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Button, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import type { PreLoadAppointmentResponse } from '@/types/scheduling-api';
import type { PatientVital } from '@/types/vitals-api';
import { useEncounterByAppointment } from '@/scheduling/services/use-encounter-by-appointment';
import { useEncounterVitals } from '@/scheduling/services/use-encounter-vitals';
import { usePatientVitalsList } from '@/scheduling/services/use-patient-vitals-list';
import { useCreateEncounter } from '@/scheduling/services/use-create-encounter';
import { useCreateVitals } from '@/scheduling/services/use-create-vitals';
import { useUpdateVitals } from '@/scheduling/services/use-update-vitals';
import { useVitalsRealtime } from '@/scheduling/realtime/use-vitals-realtime';
import { VitalCell } from './vitals/vital-cell';
import { lmpIsMenstrualFrom } from './vitals/lmp-flags';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { VitalsHistorySlot } from '@/encounter/vitals-history/vitals-history-slot';
import { VitalsGrowthChart, growthChartPatientFrom, type PreloadGrowthPatient } from './vitals/vitals-growth-chart';
import { filterCatalogForPatient } from './vitals/vital-catalog';
import { parseAgeFromText } from './vitals/parse-age';
import { validateVitalsForm } from './vitals/vital-validation';
import { VITAL_CODES } from './vitals/vital-codes';
import { injectDerivedReadings } from './vitals/vital-derive';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * ViewAppointmentVitalsTab — Vitals tab body for the appointment panel
 * (PROD-795).
 *
 * PR sequence:
 *   - PR 1 (shipped #151): data layer + read-only grid
 *   - **PR 2 (this commit):** edit mode + Save / Cancel + create-vitals
 *     mutation. The encounter create-on-demand chain is wired so the
 *     first Save creates an encounter if none exists yet (same
 *     pattern as the Docs tab PROD-797 PR 6).
 *   - PR 3: Visual Acuity / LMP / HCM special-cell editors
 *   - PR 4 (deferred per ticket): vitals history drawer + growth chart
 *   - PR 5: SignalR `UpdateVitals` listener
 *
 * Save flow (mirrors Flutter `vitals_tab.dart:572-604`):
 *   1. Validate form values per `vital_utils.dart` rules. Validation
 *      fires ONLY on Save click — never on input change or blur, so a
 *      user tabbing between BP systolic and diastolic doesn't see a
 *      transient error mid-entry (Samer review PR #153 §4).
 *   2. If no encounter yet → fire `useCreateEncounter`. On success, the
 *      cache writes the new identifier (see `use-create-encounter.ts`
 *      `setQueryData` race-fix) and the chain falls through to step 3
 *      with the resolved id.
 *   3. Build the vitals array — one row per catalog code. Each row
 *      gets `vitalNormalStatus: null` + `vitalAbnormalPercentage: null`
 *      so the server's normalization service recomputes from the new
 *      value (patient-safety commitment from PR 1 Samer review §5).
 *   4. Branch on `readings[0]?.identifire`:
 *        - null → POST CreateVitals (server assigns identifiers)
 *        - set  → PUT updateVitals (we echo the existing identifiers)
 *
 * Failure-mode contract (Samer review PR #153 §2):
 *   - If `createEncounter` fails: no setQueryData fires, cache
 *     unchanged. The user sees the "no encounter yet" copy. Retry
 *     creates from scratch — no stale state.
 *   - If `createEncounter` succeeds but `createVitals`/`updateVitals`
 *     fails: the new encounter is committed in our cache (and on the
 *     server). The vitals query is NOT invalidated (mutation onSuccess
 *     never ran). On retry, `useEncounterByAppointment` returns the
 *     same encounterId from cache, the chain skips step 2, and step 4
 *     re-fires the same mutation against the same encounter. The form
 *     state is preserved (we only reset on success), so no user input
 *     is lost.
 *   - The orphan-encounter case (encounter created, vitals never
 *     followed) is an accepted cost on this flow — same as PROD-797
 *     PR 6's Docs-tab create-on-demand. Backend hygiene cleans up
 *     encounters with no clinical content.
 *
 * Permission gate: the panel page (`view-appointment-panel-page.tsx:88`)
 * already gates the tab on `Manage_Vitals.view`. Edit access requires
 * the same permission with `edit: true` — checked at the Edit button.
 */

interface ViewAppointmentVitalsTabProps {
  data: PreLoadAppointmentResponse | null;
}

const DEFAULT_FORM_VALUES: Record<string, string> = {};

function findReading(
  readings: PatientVital[] | undefined,
  code: string,
): PatientVital | null {
  if (!readings) return null;
  return readings.find((r) => r.code?.code === code) ?? null;
}

/**
 * Build the wire-shape array sent to CreateVitals / UpdateVitals.
 * For each catalog row we either reuse the existing reading (if the
 * encounter already has one for this code) and overlay the new value,
 * or construct a fresh row.
 *
 * `vitalNormalStatus` and `vitalAbnormalPercentage` are explicitly
 * cleared on every save so the server is the sole abnormal-flag
 * authority — see `use-update-vitals.ts` for the patient-safety note.
 */
function buildVitalsPayload(args: {
  catalogCodes: string[];
  formValues: Record<string, string>;
  existing: PatientVital[];
  patientId: string;
  encounterId: string;
  /** Working LMP isMenstrual flag from the form. Only used to stamp
   *  the LMP row's `isMenstrual` field. */
  lmpIsMenstrual: boolean;
}): PatientVital[] {
  const { catalogCodes, formValues, existing, patientId, encounterId, lmpIsMenstrual } = args;
  // no-Z per CLAUDE.md, and read off the LOCAL clock. PROD-1778: this was
  // `new Date().toISOString().replace(/Z$/, '')` — `toISOString()` converts to
  // UTC first, so stripping the `Z` bakes the offset in instead of removing it
  // and the backend stored a vitals reading taken at 13:00 Asia/Dubai as 09:00.
  const nowWire = formatLocalDatetimeForWire(new Date());

  return catalogCodes.map((code) => {
    const prior = existing.find((r) => r.code?.code === code);
    const rawValue = formValues[code];
    // Empty / whitespace-only → wire `null`, not `""` and not a
    // missing key. Hearing's `combineHearingValue` already returns
    // `null` when both sides are unset; that null becomes `""` in
    // the cell's onChange handler (so `formValues[code]` is `""`),
    // which this normalization then converts back to `null` for the
    // wire. End contract: Hearing-empty ships as `quantity.value: null`.
    // Samer review PR #154 §2.
    const value = rawValue == null || rawValue.trim() === '' ? null : rawValue;

    // LMP cell: the row's `isMenstrual` flag tracks the form toggle,
    // not the prior server value. Flutter `vitals_tab.dart:516-538`.
    const isLmp = code === VITAL_CODES.LMP;
    const rowIsMenstrual = isLmp
      ? lmpIsMenstrual
      : prior?.isMenstrual ?? true;

    return {
      // `identifire` is the backend's misspelled field name — NOT a
      // typo in our code. The .NET model binder is case-sensitive and
      // rejects `identifier`. See `src/types/vitals-api.ts` for the
      // wire contract. Do not "fix" this.
      identifire: prior?.identifire ?? null,
      status: prior?.status ?? null,
      code: prior?.code ?? {
        code,
        // Capital D — backend ships `Definition` with PascalCase here
        // only (the rest of the Code object is camelCase).
        Definition: null,
        display: null,
        system: null,
        description: null,
        shortName: null,
      },
      quantity: {
        value,
        unit: null,
        system: null,
        code,
      },
      subject: patientId,
      encounter: encounterId,
      category: null,
      dataAbsentReason: null,
      deleteProvider: null,
      effectiveDateTime: nowWire,
      // Patient-safety: null out and let server recompute.
      vitalNormalStatus: null,
      vitalAbnormalPercentage: null,
      lmp: prior?.lmp ?? null,
      hcm: prior?.hcm ?? null,
      isDeleted: prior?.isDeleted ?? false,
      createdDate: prior?.createdDate ?? nowWire,
      isMenstrual: rowIsMenstrual,
      // PROD-3163 (Andrew, 9 Oct): a NEW row says "not breast feeding". The old `?? true` copied Flutter's model
      // default, which Flutter never edits (its switch is a device preference), so every new row recorded
      // "breast feeding". Scrips.Patient only stores the field (no rule, not sent to NABIDH/Malaffi); a stored value is kept.
      isBreastFeeding: prior?.isBreastFeeding ?? false,
      // PR 3 will populate extraData for Visual Acuity. PR 2 echoes
      // the existing blob when present so we don't clobber it.
      extraData: prior?.extraData ?? null,
    };
  });
}

export function ViewAppointmentVitalsTab({
  data,
}: ViewAppointmentVitalsTabProps) {
  const appointment = data?.appointment ?? null;
  const appointmentId = appointment?.id;

  // Pull patient id from participant (slim shape — id is enough for the
  // vitals query). Demographics for gating come from patientData below.
  const patientId = findPatientParticipant(appointment?.participant)?.patient?.id;
  const participantPatient = findPatientParticipant(appointment?.participant)?.patient;
  const patientName = participantPatient?.name || [participantPatient?.firstName, participantPatient?.lastName].filter(Boolean).join(' ');

  // Extract gender + age from PreLoadAppointmentDetails patientData.
  // The full-shape patient lives at `data.patientData.patient` with
  // `genderName: { id, name: "Male" | "Female" }`. Falls back to the
  // slim participant shape (age only, no gender) for tabs opened
  // before the preload settles.
  const fullPatient = data?.patientData?.patient as
    | {
        age?: string;
        genderName?: { name?: string };
      } & PreloadGrowthPatient
    | undefined;
  const age = parseAgeFromText(fullPatient?.age);
  const genderRaw = fullPatient?.genderName?.name?.toLowerCase();
  const gender: 'male' | 'female' | null =
    genderRaw === 'male' || genderRaw === 'female' ? genderRaw : null;

  // Server data — encounter then vitals.
  const encounterQuery = useEncounterByAppointment({ appointmentId });
  const encounterId = encounterQuery.data?.identifier ?? null;
  const vitalsQuery = useEncounterVitals({
    patientId,
    encounterId,
    enabled: !!patientId && !!encounterId,
  });

  // Patient-wide vitals history — drives the per-cell sparkline trend
  // chart. Flutter parity: `GetPatientVitalsHistoryEvent` in
  // `vitals_tab.dart:113-123`. Page size 36 by default (mirrors
  // Flutter's heuristic; the cell only renders the last few points
  // anyway).
  const patientVitalsQuery = usePatientVitalsList({
    patientId,
    enabled: !!patientId,
  });
  const patientVitalsHistory =
    patientVitalsQuery.data?.vitalHistoryRequest ?? [];

  // Mutations.
  const createEncounter = useCreateEncounter();
  const createVitals = useCreateVitals();
  const updateVitals = useUpdateVitals();

  const qc = useQueryClient();

  // Edit state. `formValues` is the working copy keyed by code; only
  // populated while editing — when not editing, the read-only render
  // pulls straight from `vitalsQuery.data`.
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>(
    DEFAULT_FORM_VALUES,
  );

  // SignalR — listen for vitals updates from other devices / staff
  // editing the same encounter. Invalidates both the per-encounter
  // vitals query and the history query so the tab + drawer refresh.
  // The `paused: editing` flag (Samer PR #158 §2) enforces the mid-
  // edit guard inside the hook itself, so a partial-form refresh
  // can't clobber the user's in-progress values even if a future
  // consumer of this hook forgets to short-circuit in their callback.
  useVitalsRealtime({
    appointmentId,
    encounterId,
    patientId,
    paused: editing,
    onVitalsUpdate: (payload) => {
      const pid = payload.patientId ?? patientId;
      const eid = payload.encounterId ?? encounterId;
      if (!pid || !eid) return;
      void qc.invalidateQueries({ queryKey: ['encounter-vitals', eid, pid] });
      void qc.invalidateQueries({
        queryKey: ['encounter-vitals-history', eid, pid],
      });
    },
  });
  /**
   * LMP-only state — the `isMenstrual` flag on the LMP row. Tracked
   * separately because it isn't a string value. Seed from the server's
   * existing LMP row when entering edit mode; defaults to true
   * (Flutter's `fromJson` defaults missing isMenstrual to true).
   */
  const [lmpIsMenstrual, setLmpIsMenstrual] = useState(true);

  const catalog = useMemo(() => filterCatalogForPatient(gender, age), [
    gender,
    age,
  ]);
  const catalogCodes = useMemo(() => catalog.map((e) => e.code), [catalog]);

  // Inject client-derived BMI + W/H Ratio (same helper used by the
  // history drawer). The server doesn't persist BMI as a row — Flutter
  // computes it from height + weight via `vital_data.dart:bmi(list)`.
  // We mirror that so the in-tab grid matches the clinical UX of the
  // Flutter Historical view. `injectDerivedReadings` returns null when
  // inputs are missing or zero, so cells with no height/weight stay
  // empty (matches the "if not captured, no BMI" rule).
  const readings = useMemo(
    () => injectDerivedReadings(vitalsQuery.data ?? []),
    [vitalsQuery.data],
  );

  // Seed the form when entering edit mode from the server response.
  // Re-seed on every Edit click so cancelling-then-re-editing picks up
  // any background refresh from another device.
  useEffect(() => {
    if (!editing) return;
    const seed: Record<string, string> = {};
    catalogCodes.forEach((code) => {
      seed[code] = findReading(readings, code)?.quantity?.value ?? '';
    });
    setFormValues(seed);
    // Seed isMenstrual from the prior LMP row (lmpIsMenstrualFrom): the stored flag, else false
    // for a stored "Not menstruating", else true (Flutter parity — `fromJson` treats missing
    // isMenstrual as true). D-5101: `?? true` alone opened a stored "Not menstruating" as
    // menstruating and saved the two together.
    const priorLmp = findReading(readings, VITAL_CODES.LMP);
    setLmpIsMenstrual(lmpIsMenstrualFrom(priorLmp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Patient-switch safety (Samer review PR #154 §4). The panel page
  // doesn't necessarily unmount this tab when the URL appointmentId
  // changes — React Router re-renders the route component, but the
  // tab's local state (editing flag, formValues, lmpIsMenstrual)
  // survives unless we explicitly reset it. A stale `editing=true` +
  // formValues from patient A would silently pre-populate patient B's
  // Vitals tab — clinically unsafe.
  //
  // On appointmentId change: drop edit mode, clear the working form,
  // reset the LMP toggle to its default. The next `editing=true`
  // re-seeds from the new patient's server data via the effect above.
  useEffect(() => {
    setEditing(false);
    setFormValues(DEFAULT_FORM_VALUES);
    setLmpIsMenstrual(true);
  }, [appointmentId]);

  if (!patientId) {
    return (
      <div data-slot="appt-tab-page">
        <p className="text-sm text-text-muted">
          Couldn't identify the patient for this appointment.
        </p>
      </div>
    );
  }

  const isInitialLoading = encounterQuery.isLoading || vitalsQuery.isLoading;
  const hasError = encounterQuery.isError || vitalsQuery.isError;
  const noEncounterYet = encounterQuery.isFetched && !encounterId;

  const isMutating =
    createEncounter.isPending || createVitals.isPending || updateVitals.isPending;

  const onCancel = () => {
    setEditing(false);
    setFormValues(DEFAULT_FORM_VALUES);
    setLmpIsMenstrual(true);
  };

  const onSave = async () => {
    const error = validateVitalsForm(formValues);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      // Resolve encounter — create on demand if none exists.
      let resolvedEncounterId = encounterId;
      if (!resolvedEncounterId) {
        if (!appointment) {
          toast.error('Cannot save vitals — appointment not loaded.');
          return;
        }
        const result = await createEncounter.mutateAsync({ appointment });
        resolvedEncounterId = result.identifier;
      }

      const payload = buildVitalsPayload({
        catalogCodes,
        formValues,
        existing: readings,
        patientId,
        encounterId: resolvedEncounterId,
        lmpIsMenstrual,
      });

      // Branch — first save is create, subsequent are update. Flutter
      // checks the first vital's identifire as the proxy for "has this
      // encounter ever had vitals saved" (vitals_tab.dart:594-599).
      const isFirstSave = !readings[0]?.identifire;
      if (isFirstSave) {
        await createVitals.mutateAsync({
          patientId,
          encounterId: resolvedEncounterId,
          vitals: payload,
        });
      } else {
        await updateVitals.mutateAsync({
          patientId,
          encounterId: resolvedEncounterId,
          vitals: payload,
        });
      }

      toast.success('Vitals saved');
      setEditing(false);
      setFormValues(DEFAULT_FORM_VALUES);
      setLmpIsMenstrual(true);
    } catch {
      /* Global toast handler surfaces the failure; keep form open so
         the user can retry without losing input. */
    }
  };

  return (
    <div data-slot="appt-tab-page">
      {/* Header: the section title (section role, plain ink) and the actions: one primary (Save while editing), the rest secondary. */}
      <div data-slot="appt-tab-head">
        <h2 data-slot="appt-tab-title">
          Vitals
        </h2>
        <div data-slot="appt-tab-actions">
          {isInitialLoading ? (
            <BrandSpinner size={20} label="Loading vitals…" />
          ) : null}
          {editing ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={onCancel}
                disabled={isMutating}
                className="gap-1"
              >
                <X className="size-4" aria-hidden />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                disabled={isMutating}
                className="gap-1"
              >
                {isMutating ? (
                  <BrandSpinner size={16} label="Saving" />
                ) : <Check className="size-4" aria-hidden />}
                {isMutating ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {/* "View Vitals +" — Flutter parity: text-link style on
                  the right with a + icon next to it. Disabled when
                  there's no encounter yet (history isn't meaningful). */}
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                disabled={!encounterId}
                data-slot="appt-tab-link"
                title={
                  encounterId
                    ? undefined
                    : 'History available once an encounter exists'
                }
              >
                View Vitals
                <Plus className="size-4" aria-hidden />
              </button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditing(true)}
                disabled={!encounterQuery.isFetched}
                className="gap-1"
              >
                <Pencil className="size-4" aria-hidden />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {noEncounterYet && !editing ? (
        <p className="mb-3 text-xs text-text-secondary">
          No encounter for this appointment yet — clicking Edit and saving
          your first vital will create one automatically.
        </p>
      ) : null}

      {hasError ? (
        <p className="mb-3 text-sm text-status-error">
          Couldn't load vitals. The global error toast has the details.
        </p>
      ) : null}

      <div data-slot="appt-vitals-grid">
        {catalog.map((entry) => (
          <VitalCell
            key={entry.code}
            compact
            entry={entry}
            reading={findReading(readings, entry.code)}
            allReadings={readings}
            editing={editing}
            formValues={formValues}
            onValueChange={(code, value) =>
              setFormValues((prev) => ({ ...prev, [code]: value }))
            }
            lmpIsMenstrual={lmpIsMenstrual}
            onLmpMenstrualChange={setLmpIsMenstrual}
            historicalReadings={patientVitalsHistory}
          />
        ))}
      </div>

      <VitalsGrowthChart
        patientId={patientId}
        ageYears={age}
        sex={gender}
        {...growthChartPatientFrom(fullPatient)}
      />

      <VitalsHistorySlot
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        patientId={patientId}
        encounterId={encounterId}
        patientName={patientName}
        female={gender === 'female'}
        onRecord={encounterQuery.isFetched ? () => setEditing(true) : undefined}
      />
    </div>
  );
}
